import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { TextBlock } from '../../Atoms/Text';
import { ScheduleFormFields } from '../CustomTableViews/ScheduleFormFields';
import { ScheduleSummary } from '../CustomTableViews/ScheduleSummary';
import { IncludedTabsField } from './IncludedTabsField';
import { ReportNameField } from './ReportNameField';
import { OrdersReport } from './ordersReport';
import { useOrdersReportForm } from './useOrdersReportForm';

const INTRO_ID = 'orders-schedule-report-intro';

export interface OrdersScheduleReportModalProps {
  open: boolean;
  /** The Orders report to edit; without one, Save creates a report. */
  existingSchedule?: OrdersReport | null;
  onClose: () => void;
}

/** Creates or edits an Orders report: a schedule that emails the orders of the workflow tabs it includes. */
export const OrdersScheduleReportModal = ({
  open,
  existingSchedule = null,
  onClose
}: OrdersScheduleReportModalProps) => {
  const {
    scheduleForm,
    reportKey,
    reportName,
    setReportName,
    includedTabs,
    setIncludedTabs,
    preview,
    saving,
    canSave,
    save
  } = useOrdersReportForm(open, existingSchedule, onClose);

  return (
    <Dialog
      data-testid="orders-schedule-report-modal"
      aria-describedby={INTRO_ID}
      maxWidth="sm"
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          Schedule Report
        </TextBlock>
      </DialogTitle>
      <DialogContent className="text-sm">
        <TextBlock
          id={INTRO_ID}
          size="sm"
          color="grey6"
          className="mb-4">
          Choose which Orders tabs to pull data from, then set how often to send this report.
        </TextBlock>
        <ReportNameField
          id={`report-name-${reportKey}`}
          value={reportName}
          disabled={saving}
          onChange={setReportName}
        />
        <IncludedTabsField
          value={includedTabs}
          disabled={saving}
          onChange={setIncludedTabs}
        />
        <ScheduleFormFields
          viewKey={reportKey}
          scheduleForm={scheduleForm}
          disabled={saving}
          recipientsLabel="Report Recipients"
          sendToSelfLabel="Also send scheduled Report to me"
        />
        <ScheduleSummary
          form={scheduleForm.form}
          preview={preview}
          paused={!!existingSchedule?.paused_at}
          hasEndDate={scheduleForm.endDateValue !== null}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onClose}
          disabled={saving}>
          Cancel
        </button>
        <button
          data-testid="orders-schedule-report-save"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={save}
          disabled={saving || !canSave}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
